import { useMemo } from 'react';
import { useStore } from '../../store';
import type { TableConfig } from '../../types/visualizations';

interface TableBlockProps {
  config: TableConfig;
}

export default function TableBlock({ config }: TableBlockProps) {
  const { parsedDatasets } = useStore();

  const dataset = useMemo(() => {
    return parsedDatasets.find((d) => d.id === config.datasetId);
  }, [parsedDatasets, config.datasetId]);

  const columns = useMemo(() => {
    if (!dataset) return [];
    // Use specified columns or all columns
    return config.columns || dataset.headers;
  }, [dataset, config.columns]);

  const rows = useMemo(() => {
    if (!dataset) return [];
    let result = dataset.rows;
    // Apply row limit if specified
    if (config.maxRows && config.maxRows > 0) {
      result = result.slice(0, config.maxRows);
    }
    return result;
  }, [dataset, config.maxRows]);

  if (!dataset || rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <p className="text-sm text-gray-400">No data available</p>
      </div>
    );
  }

  return (
    <div className="table-block overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={config.striped && rowIndex % 2 === 1 ? 'bg-gray-50' : ''}
            >
              {columns.map((col) => (
                <td
                  key={col}
                  className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap"
                >
                  {formatCellValue(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {config.maxRows && dataset.rows.length > config.maxRows && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
          Showing {config.maxRows} of {dataset.rows.length} rows
        </div>
      )}
    </div>
  );
}

function formatCellValue(value: string | number | undefined | null): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'number') {
    // Format numbers nicely
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
}
